// ── 상황판 통합 콘텐츠 (PC + 모바일) — 2026-06-09 통합 ─────────────────────────
//
// 사용자 명세 (2026-06-09):
//   본인 위젯 + 큰 카드 + admin 위젯 — 한 grid / 한 SortableContext / 사이즈 무관 자유 자리변경.
//   두 영역(personal / admin) 분리 제거.
//
// 사이즈 매핑:
//   본인 위젯 (my_*) = col-span-2 (MiniRow 가 결정)
//   S = col-span-1 (KPI 4종)
//   M = col-span-2 (출퇴근/콜·계약/연차/카드결제/다음방문)
//   L = col-span-2 lg:col-span-3 (홍보물/정산/접속/계약/지도/최근계약)
//
// preference 키:
//   신규 단일 키 `situation_widget_order` — 모든 위젯(my_* + 큰 카드 + admin_*) 통합 순서
//   레거시 두 키 (`situation_widget_order_personal`, `situation_widget_order_admin`) read 호환:
//     useSituationWidgets.js 에서 신규 키 우선, 없으면 두 레거시 키 merge.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext, closestCenter,
  PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { PERSONAL_WIDGET_CODES, renderPersonalCell } from './PersonalMiniSection';
import { AdminWidgetCard } from './AdminWidgets';
import { hasAdminSpec } from './AdminWidgetsSpec';
import { reorderByPreference } from './useSituationWidgets';
import { renderStatsCard } from './bigCards/renderStats';
import { renderTableCard } from './bigCards/renderTables';
import { parseContractWidgetCode } from './contractWidgetCodes';
import { ScopedContractWidget } from './ScopedContractWidget';

const PERSONAL_CODE_SET = new Set(PERSONAL_WIDGET_CODES);

const STATS_CODES = new Set([
  'kpi_employees', 'kpi_external_users', 'kpi_sales_roster', 'kpi_online_users',
  'attendance_today', 'month_call_contract',
  'leave_status', 'today_card_revenue',
  'next_business_meetings',
]);

const TABLE_CODES = new Set([
  'promotional_profit', 'settlement_net',
  'recent_online',
  // 2026-06-07: contracts_by_person → marketer / consultant 분리
  'contracts_by_marketer', 'contracts_by_consultant',
  'today_meeting_map', 'recent_contracts_mobile',
]);

// 큰 카드 가시 코드 — visibleSet + 데이터 의존 조건
function visibleBigCardCodes(visibleSet, stats, isMobile) {
  const result = [];
  const all = [...STATS_CODES, ...TABLE_CODES];
  for (const code of all) {
    if (!visibleSet.has(code)) continue;
    // recent_contracts_mobile 은 데이터 없으면 항상 숨김
    if (code === 'recent_contracts_mobile' && (!stats.mobileTopContracts || stats.mobileTopContracts.length === 0)) continue;
    // 모바일에서 settlement_net / today_card_revenue 은 데이터 없으면 숨김 (기존 동작 보존)
    if (isMobile && code === 'today_card_revenue' && !stats.cardRevenue) continue;
    if (isMobile && code === 'settlement_net' && (!stats.settlementNet || stats.settlementNet.length === 0)) continue;
    result.push(code);
  }
  return result;
}

function renderBigCard(code, ctx) {
  if (STATS_CODES.has(code)) return renderStatsCard(code, ctx);
  if (TABLE_CODES.has(code)) return renderTableCard(code, ctx);
  return null;
}

export function SbContent({
  stats, handlers, visibleWidgetCodes = [], personal = null, admin = null,
  accentMap = {}, orderUnified = [], setWidgetOrder, isMobile = false,
  widgetMenuMap = {}, noAccessCodes = [], summaryCodes = null, visitCategories = [],
}) {
  const navigate = useNavigate();
  const visibleSet = new Set(visibleWidgetCodes);

  // sensors — 사용자 명세 (2026-06-16): 짧은 클릭 = navigate / 500ms+ 누름 = 드래그.
  //   PointerSensor / TouchSensor 모두 delay 500ms 통일.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 500, tolerance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 500, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // 위젯 코드 → onClick(navigate) 헬퍼. mapping dict 에 없으면 onClick 없음(드래그만).
  //   그 메뉴를 못 여는 위젯(손님 테넌트 직원, 2026-10-24)은 같은 주소로 가되 메뉴 자리에 "이 메뉴는 담당자가 쓰는 화면이에요"
  //   (AdminLayout 이 location.state.widgetNoAccess 를 보고 화면 대신 안내를 그린다).
  const noAccessSet = new Set(noAccessCodes);
  const navOnClickFor = (code) => {
    const url = widgetMenuMap && widgetMenuMap[code];
    if (!url) return undefined;
    if (noAccessSet.has(code)) return () => navigate(url, { state: { widgetNoAccess: true } });
    return () => navigate(url);
  };

  // ── 가시 코드 수집 (3 영역 통합) ────────────────────────────────────────────
  // personal codes — visibleSet ∩ PERSONAL_WIDGET_CODES
  const personalCodes = personal
    ? PERSONAL_WIDGET_CODES.filter((c) => visibleSet.has(c))
    : [];
  // 큰 카드 codes — 가시 + 데이터 조건
  const bigCardCodes = visibleBigCardCodes(visibleSet, stats, isMobile);
  // 회사별·통합 계약 위젯(2026-10-19) — 본사 계약 위젯과 같은 큰 카드, 숫자는 그 화면 주소
  const contractCodes = (visibleWidgetCodes || []).filter((c) => parseContractWidgetCode(c));
  // admin codes (admin_*) — registry 에 등록된 위젯만 (정적 + 통합 위젯 동적 spec)
  const adminCodes = (visibleWidgetCodes || []).filter((c) => hasAdminSpec(c));

  // 통합 codes — 기본 순서: personal → bigCards → admin
  // 사용자가 드래그하면 orderUnified 가 그 순서로 덮어씀.
  const combinedCodes = [...personalCodes, ...bigCardCodes, ...contractCodes, ...adminCodes];
  const orderedCombined = reorderByPreference(combinedCodes, orderUnified);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (!setWidgetOrder) return;
    const oldIndex = orderedCombined.indexOf(active.id);
    const newIndex = orderedCombined.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = [...orderedCombined];
    const [moved] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, moved);
    setWidgetOrder(next);
  };

  // summarySet(2026-10-24): 카드 요약만 받는 위젯 — 계약 표가 요약 길에서 그달 표를 받게(renderTables)
  const ctx = { stats, handlers, accentMap, isMobile, navOnClickFor, summarySet: summaryCodes, visitCategories };
  const ov = (code) => accentMap[code] || undefined;

  const renderItem = (code) => {
    // 1) 본인 위젯 (my_*) — navOnClickFor 로 navigate 주입
    if (PERSONAL_CODE_SET.has(code)) {
      if (!personal) return null;
      return renderPersonalCell(code, personal, ov, navOnClickFor);
    }
    // 2) 큰 카드 (KPI / 통계 / 테이블) — onClick 주입은 카드 자체가 handle
    if (STATS_CODES.has(code) || TABLE_CODES.has(code)) {
      return renderBigCard(code, ctx);
    }
    // 3) 회사별·통합 계약 위젯 — 누르면 그 계약 화면(widgetMenuMap)
    const contractWidget = parseContractWidgetCode(code);
    if (contractWidget) {
      return (
        <ScopedContractWidget
          key={code}
          code={code}
          parsed={contractWidget}
          accentOverride={accentMap[code]}
          sortableId={code}
          onClick={navOnClickFor(code)}
        />
      );
    }
    // 4) admin_* 위젯 (registry) — onClick navigate 주입
    if (hasAdminSpec(code)) {
      return (
        <AdminWidgetCard
          key={code}
          code={code}
          data={admin?.data?.[code]}
          loading={admin?.loading}
          month={admin?.monthMap?.[code]}
          onMonthChange={admin?.setMonthForCode ? (m) => admin.setMonthForCode(code, m) : undefined}
          accentOverride={accentMap[code]}
          sortableId={code}
          onClick={navOnClickFor(code)}
        />
      );
    }
    return null;
  };

  if (orderedCombined.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedCombined} strategy={rectSortingStrategy}>
          {/* 정사각 비율 grid — 모바일 row 190px (출퇴근 통계 NamePills 잘림 방지) / 데스크탑(lg+) 220px. */}
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 auto-rows-[190px] lg:auto-rows-[220px]">
            {orderedCombined.map((code) => renderItem(code))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
